(() => {
  // utils/constants.ts
  var PUB_SUB_EVENTS = {
    cartUpdate: "cart-update",
    quantityUpdate: "quantity-update",
    optionValueSelectionChange: "option-value-selection-change",
    variantChange: "variant-change",
    cartError: "cart-error",
  };

  // utils/pubsub.ts
  var subscribers = {};
  var subscribe = (eventName, callback) => {
    if (subscribers[eventName] === undefined) {
      subscribers[eventName] = [];
    }
    subscribers[eventName] = [...subscribers[eventName], callback];
    return function unsubscribe() {
      subscribers[eventName] = subscribers[eventName].filter((cb) => {
        return cb !== callback;
      });
    };
  };

  // assets/scripts/wishlist.ts
  class WishlistManager {
    storageKey = "emeline_wishlist";
    constructor() {
      this.init();
    }
    init() {
      this.refresh();
      document.addEventListener("click", (event) => {
        const target = event.target;
        const button = target?.closest(".wishlist-button");
        if (button) this.handleToggle(event, button);
      });
      new MutationObserver(() => this.refresh()).observe(document.body, {
        childList: true,
        subtree: true,
      });
      if (
        typeof subscribe !== "undefined" &&
        typeof PUB_SUB_EVENTS !== "undefined"
      ) {
        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const button = document.querySelector(
            ".product-form__buttons-with-wishlist .wishlist-button",
          );
          if (button && event.data?.variant) {
            button.dataset.variantId = event.data.variant.id;
            this.updateButtonState(button);
          }
        });
      }
    }
    refresh() {
      document
        .querySelectorAll(".wishlist-button")
        .forEach((button) => this.updateButtonState(button));
    }
    getWishlist() {
      try {
        const wishlist = localStorage.getItem(this.storageKey);
        return wishlist ? JSON.parse(wishlist) : [];
      } catch (e) {
        console.error("Error reading wishlist:", e);
        return [];
      }
    }
    saveWishlist(wishlist) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
        return true;
      } catch (e) {
        console.error("Error saving wishlist:", e);
        return false;
      }
    }
    isInWishlist(productId, variantId) {
      const wishlist = this.getWishlist();
      return wishlist.some(
        (item) => item.productId === productId && item.variantId === variantId,
      );
    }
    addToWishlist(productId, variantId, productHandle) {
      const wishlist = this.getWishlist();
      if (this.isInWishlist(productId, variantId)) {
        return false;
      }
      wishlist.push({
        productId,
        variantId,
        productHandle,
        addedAt: new Date().toISOString(),
      });
      return this.saveWishlist(wishlist);
    }
    removeFromWishlist(productId, variantId) {
      const wishlist = this.getWishlist();
      const filtered = wishlist.filter(
        (item) =>
          !(item.productId === productId && item.variantId === variantId),
      );
      return this.saveWishlist(filtered);
    }
    handleToggle(e, button) {
      e.preventDefault();
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      const productHandle = button.dataset.productHandle;
      if (!productId || !variantId || !productHandle) return;
      if (this.isInWishlist(productId, variantId)) {
        this.removeFromWishlist(productId, variantId);
      } else {
        this.addToWishlist(productId, variantId, productHandle);
      }
      this.refresh();
      const grid = button.closest("[data-wishlist-grid]");
      if (
        grid &&
        !grid.hasAttribute("data-wishlist-shared") &&
        !this.isInWishlist(productId, variantId)
      ) {
        const item =
          button.closest(".wishlist-page__item") ??
          button.closest(".product-card");
        item?.remove();
        syncPageState();
      }
    }
    updateButtonState(button) {
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      if (!productId || !variantId) return;
      const isActive = this.isInWishlist(productId, variantId);
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", String(isActive));
      button.setAttribute(
        "aria-label",
        isActive ? "Remove from wishlist" : "Add to wishlist",
      );
    }
    getWishlistCount() {
      return this.getWishlist().length;
    }
  }
  var SECTION_ID = "wishlist-card";
  var getRoot = () => window.Shopify?.routes?.root ?? "/";
  var fetchCard = async (item) => {
    const url = `${getRoot()}products/${item.productHandle}?variant=${item.variantId}&section_id=${SECTION_ID}`;
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) return "";
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.querySelector(".shopify-section")?.innerHTML ?? "";
  };
  var SHARE_PARAM = "w";
  var SHARE_MAX_ITEMS = 20;
  var encodeShareList = (items) => {
    return items
      .slice(0, SHARE_MAX_ITEMS)
      .map((item) => `${item.productHandle}:${item.variantId}`)
      .join(",");
  };
  var decodeShareList = (value) => {
    return value
      .split(",")
      .map((pair) => {
        const [productHandle, variantId] = pair.split(":");
        return productHandle && variantId
          ? {
              productHandle,
              variantId,
            }
          : null;
      })
      .filter((item) => item !== null);
  };
  var getSharedList = () => {
    const value = new URLSearchParams(window.location.search).get(SHARE_PARAM);
    return value ? decodeShareList(value) : null;
  };
  var buildShareUrl = (items) => {
    const url = new URL(window.location.href);
    url.search = `?${SHARE_PARAM}=${encodeShareList(items)}`;
    return url.toString();
  };
  var syncPageState = () => {
    const grid = document.querySelector("[data-wishlist-grid]");
    if (!grid) return;
    const empty = document.querySelector("[data-wishlist-empty]");
    const hasCards = grid.children.length > 0;
    grid.hidden = !hasCards;
    if (empty) empty.hidden = hasCards;
  };
  var matchSkeletonCount = (grid, count) => {
    const template = grid.firstElementChild;
    if (!template) return;
    while (grid.children.length > count) grid.lastElementChild?.remove();
    while (grid.children.length < count) {
      grid.appendChild(template.cloneNode(true));
    }
  };
  var renderWishlistPage = async (manager) => {
    const grid = document.querySelector("[data-wishlist-grid]");
    if (!grid) return;
    const shared = getSharedList();
    const items =
      shared ??
      manager.getWishlist().sort((a, b) => b.addedAt.localeCompare(a.addedAt));
    grid.toggleAttribute("data-wishlist-shared", Boolean(shared));
    const sharedNote = document.querySelector("[data-wishlist-shared-note]");
    const actions = document.querySelector("[data-wishlist-actions]");
    if (sharedNote) sharedNote.hidden = !shared;
    if (actions) actions.hidden = Boolean(shared) || items.length === 0;
    if (items.length === 0) {
      grid.innerHTML = "";
      syncPageState();
      return;
    }
    const hasSkeletons =
      grid.firstElementChild?.classList.contains("product-card-skeleton") ??
      false;
    if (hasSkeletons) matchSkeletonCount(grid, items.length);
    const results = await Promise.all(items.map(fetchCard));
    if (!shared) {
      results.forEach((result, index) => {
        const item = items[index];
        if (result === null && item) {
          manager.removeFromWishlist(item.productHandle, item.variantId);
        }
      });
    }
    grid.innerHTML = results.filter(Boolean).join("");
    syncPageState();
    manager.refresh();
  };
  var setShareFeedback = (message) => {
    const feedback = document.querySelector("[data-wishlist-share-feedback]");
    if (feedback) feedback.textContent = message;
  };
  var shareWishlist = async (manager, button) => {
    const items = manager.getWishlist();
    if (items.length === 0) return;
    const url = buildShareUrl(items);
    if (items.length > SHARE_MAX_ITEMS) {
      setShareFeedback(`Sharing your ${SHARE_MAX_ITEMS} most recent items`);
    }
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: document.title, url });
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareFeedback(button.dataset.copiedLabel ?? "Link copied");
    } catch {
      window.prompt("Copy your wishlist link:", url);
    }
  };
  var saveSharedList = async (manager) => {
    document
      .querySelectorAll("[data-wishlist-grid] .wishlist-button")
      .forEach((button) => {
        const { productId, variantId, productHandle } = button.dataset;
        if (productId && variantId && productHandle) {
          manager.addToWishlist(productId, variantId, productHandle);
        }
      });
    const url = new URL(window.location.href);
    url.searchParams.delete(SHARE_PARAM);
    window.history.replaceState(
      {},
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    await renderWishlistPage(manager);
  };
  var initializeWishlist = () => {
    const manager = new WishlistManager();
    window.wishlistManager = manager;
    renderWishlistPage(manager);
    document.addEventListener("click", (event) => {
      const target = event.target;
      const shareButton = target?.closest("[data-wishlist-share]");
      if (shareButton) shareWishlist(manager, shareButton);
      if (target?.closest("[data-wishlist-save-all]")) saveSharedList(manager);
    });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeWishlist);
  } else {
    initializeWishlist();
  }
})();
